// Static data for the onboarding funnel's PDF-reader sim (classical_texts
// branch): one line from Ṣaḥīḥ al-Bukhārī's first ḥadīth, with per-word
// gloss, §9 morphology, and the word-kind used for the reader's iʿrāb-mode
// box tints (verb/noun/pronoun/prep, null = untinted highlight).

export const PDF_SIM = {
  fileName: "صحيح البخاري.pdf",
  pageLabel: "Page 3 / 412",
  intro:
    "Upload any Arabic book as a PDF and every word on the scanned page becomes tappable. Here's a page you may recognise:",
  attribution: "كتاب بدء الوحي — الحديث الأول",
  sentenceTranslation:
    "Actions are only by intentions, and every person shall have only what they intended.",
  words: [
    {
      key: "w0",
      text: "إِنَّمَا",
      translit: "innamā",
      gloss: "only / no more than",
      kind: null,
      morphology: {
        label: "إِنَّمَا — only",
        segments: [
          { type: "root", text: "إِنَّمَا", labelAr: "كافة ومكفوفة", labelEn: "restriction particle", side: "bottom" },
        ],
        irab: {
          statusAr: "حرف حصر مبني لا محل له من الإعراب",
          statusEn: "Restriction particle (“only”) — indeclinable, no grammatical position.",
        },
      },
    },
    {
      key: "w1",
      text: "الْأَعْمَالُ",
      translit: "al-aʿmālu",
      gloss: "the actions / deeds",
      kind: "noun",
      morphology: {
        label: "الْأَعْمَالُ — the actions",
        segments: [
          { type: "prefix", text: "الْ", labelAr: "أل التعريف", labelEn: "definite article", side: "top" },
          { type: "root", text: "أَعْمَال", labelAr: "اسم — الجذر: ع-م-ل، جمع عَمَل", labelEn: "noun · root ʿ-m-l, plural of ʿamal", side: "bottom", hasCase: true },
        ],
        irab: {
          statusAr: "مبتدأ مرفوع وعلامة رفعه الضمة الظاهرة",
          statusEn: "Subject (mubtadaʼ), nominative — marked by the ḍammah.",
        },
      },
    },
    {
      key: "w2",
      text: "بِالنِّيَّاتِ",
      translit: "bin-niyyāti",
      gloss: "by intentions",
      kind: "noun",
      morphology: {
        label: "بِالنِّيَّاتِ — by the intentions",
        segments: [
          { type: "prefix", text: "بِ", labelAr: "حرف جر", labelEn: "preposition — “by”", side: "top" },
          { type: "prefix", text: "الْ", labelAr: "أل التعريف", labelEn: "definite article", side: "top" },
          { type: "root", text: "نِيَّات", labelAr: "اسم — جمع نِيَّة", labelEn: "noun · plural of niyyah", side: "bottom", hasCase: true },
        ],
        irab: {
          statusAr: "الباء حرف جر، النيات اسم مجرور وعلامة جره الكسرة، والجار والمجرور متعلق بخبر محذوف",
          statusEn: "بِ is a preposition; the noun is genitive (kasrah). The phrase stands as the predicate.",
        },
      },
    },
    {
      key: "w3",
      text: "وَإِنَّمَا",
      translit: "wa-innamā",
      gloss: "and only",
      kind: null,
      morphology: {
        label: "وَإِنَّمَا — and only",
        segments: [
          { type: "prefix", text: "وَ", labelAr: "حرف عطف", labelEn: "conjunction — “and”", side: "top" },
          { type: "root", text: "إِنَّمَا", labelAr: "كافة ومكفوفة", labelEn: "restriction particle", side: "bottom" },
        ],
        irab: {
          statusAr: "الواو عاطفة، وإنما حرف حصر مبني لا محل له",
          statusEn: "The و joins the clauses; the particle restricts — “and only”.",
        },
      },
    },
    {
      key: "w4",
      text: "لِكُلِّ",
      translit: "li-kulli",
      gloss: "for every",
      kind: "noun",
      morphology: {
        label: "لِكُلِّ — for every",
        segments: [
          { type: "prefix", text: "لِ", labelAr: "حرف جر", labelEn: "preposition — “for”", side: "top" },
          { type: "root", text: "كُلِّ", labelAr: "اسم", labelEn: "noun — “every”", side: "bottom", hasCase: true },
        ],
        irab: {
          statusAr: "اللام حرف جر، كل اسم مجرور وعلامة جره الكسرة وهو مضاف",
          statusEn: "لِ is a preposition; كل is genitive (kasrah) and governs the next word.",
        },
      },
    },
    {
      key: "w5",
      text: "امْرِئٍ",
      translit: "imriʼin",
      gloss: "person",
      kind: "noun",
      morphology: {
        label: "امْرِئٍ — person",
        segments: [
          { type: "root", text: "امْرِئٍ", labelAr: "اسم", labelEn: "noun — “person”", side: "bottom", hasCase: true },
        ],
        irab: {
          statusAr: "مضاف إليه مجرور وعلامة جره الكسرة الظاهرة (منوَّن)",
          statusEn: "Second part of the possessive chain, genitive — marked by kasrah tanwīn.",
        },
      },
    },
    {
      key: "w6",
      text: "مَا",
      translit: "mā",
      gloss: "what(ever)",
      kind: "noun",
      morphology: {
        label: "مَا — what(ever)",
        segments: [
          { type: "root", text: "مَا", labelAr: "اسم موصول", labelEn: "relative noun — “that which”", side: "bottom" },
        ],
        irab: {
          statusAr: "اسم موصول مبني على السكون في محل رفع مبتدأ",
          statusEn: "Relative noun (“that which”), indeclinable — in the position of a subject.",
        },
      },
    },
    {
      key: "w7",
      text: "نَوَى",
      translit: "nawā",
      gloss: "he intended",
      kind: "verb",
      morphology: {
        label: "نَوَى — he intended",
        segments: [
          { type: "root", text: "نَوَى", labelAr: "فعل ماضٍ — الجذر: ن-و-ي", labelEn: "past-tense verb · root n-w-y", side: "bottom" },
        ],
        irab: {
          statusAr: "فعل ماضٍ مبني على الفتح المقدر على الألف، والفاعل ضمير مستتر تقديره هو",
          statusEn: "Past verb, built on an implied fatḥah; hidden subject “he”.",
        },
      },
    },
  ],
};
